import { motion, useScroll, useTransform } from "framer-motion";
import React, { useRef } from "react";
import { 
  Target, 
  Calendar, 
  Zap, 
  Megaphone, 
  TrendingUp, 
  BarChart3 
} from 'lucide-react';

const processSteps = [
  {
    id: 1,
    title: 'STRUGGLING TO FIND THE RIGHT INFLUENCERS FOR YOUR BRAND?',
    icon: <Target className="w-8 h-8" />,
    bgColor: '#ff521a',
    src: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800"
  },
  {
    id: 2,
    title: 'NEED AUTHENTIC CREATOR PARTNERSHIPS THAT ACTUALLY CONVERT?',
    icon: <Calendar className="w-8 h-8" />,
    bgColor: '#c08cf2',
    src: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800"
  },
  {
    id: 3,
    title: 'WANT MEDIA COVERAGE BUT DON\'T KNOW WHERE TO START?',
    icon: <Zap className="w-8 h-8" />,
    bgColor: '#cfc4af',
    src: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800"
  },
  {
    id: 4,
    title: 'LOOKING FOR AD CAMPAIGNS THAT DELIVER RESULTS?',
    icon: <Megaphone className="w-8 h-8" />,
    bgColor: '#ffbe99',
    src: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800"
  },
  {
    id: 5,
    title: 'LET\'S GROW YOUR BRAND TOGETHER WITH LXDIA',
    icon: <TrendingUp className="w-8 h-8" />,
    bgColor: '#ffa3d1',
    src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
    hasButton: true
  }
];

// Helper function to get card background colors - your custom colors
const getCardBackground = (index) => {
  const backgrounds = [
    '#ff521a', // 1 - orange/red
    '#c08cf2', // 2 - purple
    '#cfc4af', // 3 - beige/tan
    '#ffbe99', // 4 - light orange
    '#ffa3d1'  // 5 - pink
  ];
  return backgrounds[index] || backgrounds[0];
};

const ProcessStickyCard = ({
  i,
  title,
  progress,
  range,
  targetScale,
  hasButton,
}) => {
  const container = useRef(null);
  const scale = useTransform(progress, range, [1, targetScale]);

  return (
    <div
      ref={container}
      className="sticky top-0 flex items-center justify-center px-4"
    >
      <motion.div
        style={{
          scale,
          top: `calc(-5vh + ${i * 15 + 100}px)`,
        }}
        className="relative -top-1/4 flex h-[400px] sm:h-[500px] lg:h-[600px] xl:h-[650px] w-full max-w-[99vw] sm:max-w-[97vw] lg:max-w-[95vw] xl:max-w-[93vw] origin-top flex-col overflow-hidden rounded-xl sm:rounded-2xl lg:rounded-3xl"
      >
        <div 
          className="h-full w-full hover:border-opacity-60 transition-all duration-500 shadow-2xl relative overflow-hidden group p-3 sm:p-4 lg:p-8"
          style={{
            backgroundColor: getCardBackground(i)
          }}
        >
          {/* Content Layout */}
          <div className="flex flex-col items-center justify-start h-full p-4 sm:p-6 lg:p-8 pt-16 sm:pt-20 lg:pt-24 text-center gap-8">
            {/* Bold Text Content */}
            <div className="w-full max-w-4xl flex items-center justify-center">
              <h3 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-black uppercase tracking-tight"
                style={{
                  fontWeight: 900,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  lineHeight: '1.2'
                }}
              >
                {title}
              </h3>
            </div>
            
            {/* Button for last card */}
            {hasButton && (
              <a 
                href="tel:+918287836426"
                className="bg-[#C84B8A] text-white px-8 py-4 rounded-full font-bold text-lg sm:text-xl hover:bg-[#A83D72] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Schedule a Call
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const ProcessSteps = () => {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  return (
    <section className="bg-white -mt-20 sm:mt-0">
      <div
        ref={container}
        className="relative flex w-full flex-col items-center justify-center pb-[15vh] sm:pb-[25vh] lg:pb-[35vh] pt-0"
        style={{ minHeight: '500vh' }}
      >
        {processSteps.map((step, i) => {
          const targetScale = Math.max(
            0.8,
            1 - (processSteps.length - i - 1) * 0.05,
          );
          return (
            <ProcessStickyCard
              key={`process_${i}`}
              i={i}
              title={step.title}
              progress={scrollYProgress}
              range={[i * 0.2, 1]}
              targetScale={targetScale}
              hasButton={step.hasButton}
            />
          );
        })}
      </div>
    </section>
  );
};

export default ProcessSteps;
